import {Component, ViewChild} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TableModule} from 'primeng/table';
import {combineLatest} from 'rxjs';
import {MiniBookAdminService} from '../service/mini-book-admin.service';
import {Tag} from '../shared/Tag';
import {TagCategory} from '../shared/TagCategory';
import {Button} from 'primeng/button';

@Component({
    selector: 'edu-udo-itmc-dev-mini-book-tags',
    templateUrl: './tags.component.html',
    styleUrl: './tags.component.css',
    standalone: false
})
export class TagsComponent {

  tagsPerCategory: {[categoryId: number]: Tag[]} = {};
  tagCategories: TagCategory[] = [];
  clonedTagCategories: {[id: number]: TagCategory} = {};
  clonedTags: {[id: number]: Tag} = {};

  // Required for PrimeNG v19+ row expansion
  expandedRowKeys: { [key: string]: boolean } = {};

  rowTrackBy = (tagCategory: TagCategory) => tagCategory.id;

  constructor(
    private adminService: MiniBookAdminService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    combineLatest([
      this.adminService.tags.getTagCategories(),
      this.adminService.tags.getTags()
    ])
      .subscribe({
        next: ([categories, tags]) => {
          this.tagCategories = categories;
          this.tagCategories.forEach(category => {
            this.tagsPerCategory[category.id] = tags.filter(tag => category.tagIds.includes(tag.id));
          })
        }
      })
  }

  createCategory(table: any) {
    const newCategory = new TagCategory();
    // ID bleibt 0 (oder undefined) für neue Kategorien
    this.tagCategories.push(newCategory);
    this.expandedRowKeys[0] = true; // Auto-expand new category (ID 0)
    this.tagsPerCategory[0] = []; // Initialize empty tags array for ID 0
    table.initRowEdit(newCategory);
  }

  updateCategory(category: TagCategory) {
    if (category.id === 0) {
      // Neue Kategorie erstellen
      this.adminService.tags.createTagCategory(category).subscribe({
        next: value => {
          const oldId = category.id; // 0
          category.fromJson(value.toJson());
          
          // Tags-Array auf neue ID verschieben
          this.tagsPerCategory[value.id] = this.tagsPerCategory[oldId] || [];
          delete this.tagsPerCategory[oldId];
          
          // Expansion auf neue ID aktualisieren
          if (this.expandedRowKeys[oldId]) {
            delete this.expandedRowKeys[oldId];
            this.expandedRowKeys[value.id] = true;
          }
          
          this.messageService.add({
            summary: 'Kategorie wurde gespeichert.',
            severity: 'success'
          });
        }
      });
    } else {
      this.adminService.tags.updateTagCategory(category.id, category).subscribe({
        next: value => {
          category.fromJson(value.toJson());
          this.messageService.add({
            summary: 'Kategorie wurde gespeichert.',
            severity: 'success'
          });
        },
        error: err => {
          category.fromJson(this.clonedTagCategories[category.id]);
          this.messageService.add({
            summary: 'Fehler bei der Kommunikation mit dem Backend.',
            detail: err.message,
            severity: 'error'
          });
        }
      });
      delete this.clonedTagCategories[category.id];
    }
  }

  onCategoryEditInit(category: TagCategory) {
    this.clonedTagCategories[category.id] = TagCategory.createFromJson(category.toJson());
  }

  onCategoryEditCancel(category: TagCategory) {
    if (category.id === 0) {
      // Neue Kategorie wieder entfernen
      this.tagCategories = this.tagCategories.filter(value => value.id !== 0);
      delete this.tagsPerCategory[0];
      delete this.expandedRowKeys[0];
    } else {
      category.fromJson(this.clonedTagCategories[category.id]);
      delete this.clonedTagCategories[category.id];
    }
  }

  deleteCategory(category: TagCategory) {
    this.confirmationService.confirm({
      message: 'Wollen sie die Kategorie wirklich löschen?\nDadurch gehen alle Tags in dieser Kategorie verloren!',
      header: 'Löschen bestätigen',
      acceptLabel: 'Löschen',
      acceptIcon: 'pi pi-trash mr-2',
      rejectLabel: 'Abbrechen',
      accept: () => {
        this.adminService.tags.deleteTagCategory(category.id).subscribe({
          next: () => {
            this.tagCategories = this.tagCategories.filter(value => value.id !== category.id);
            delete this.tagsPerCategory[category.id];
            delete this.expandedRowKeys[category.id];
            this.messageService.add({
              summary: 'Kategorie und zugehörige Tags wurden gelöscht.',
              severity: 'success'
            });
          }
        });
      }
    });
  }

  createTag(categoryId: number, table: any) {
    const newTag = new Tag();
    // ID bleibt 0 (oder undefined) für neue Tags
    newTag.categoryId = categoryId;
    this.tagsPerCategory[categoryId].push(newTag);
    table.initRowEdit(newTag);
  }

  updateTag(categoryId: number, tag: Tag) {
    if (tag.id === 0) {
      // Neues Tag erstellen
      this.adminService.tags.createTag(tag).subscribe({
        next: value => {
          tag.fromJson(value.toJson());
          // Replace entry with real ID
          const idx = this.tagsPerCategory[categoryId].findIndex(t => t.id === 0);
          if (idx !== -1) {
            this.tagsPerCategory[categoryId][idx] = tag;
          }
          this.messageService.add({
            summary: 'Tag wurde gespeichert.',
            severity: 'success'
          });
        }
      });
    } else {
      this.adminService.tags.updateTag(tag.id, tag).subscribe({
        next: value => {
          tag.fromJson(value.toJson());
          this.messageService.add({
            summary: 'Tag wurde gespeichert.',
            severity: 'success'
          });
        },
        error: err => {
          tag.fromJson(this.clonedTags[tag.id]);
          this.messageService.add({
            summary: 'Fehler bei der Kommunikation mit dem Backend.',
            detail: err.message,
            severity: 'error'
          });
        }
      });
      delete this.clonedTags[tag.id];
    }
  }

  onTagEditInit(tag: Tag) {
    this.clonedTags[tag.id] = Tag.createFromJson(tag.toJson());
  }

  onTagEditCancel(categoryId: number, tag: Tag) {
    if (tag.id === 0) {
      // Neues Tag wieder entfernen
      this.tagsPerCategory[categoryId] = this.tagsPerCategory[categoryId].filter(value => value.id !== 0);
    } else {
      tag.fromJson(this.clonedTags[tag.id]);
      delete this.clonedTags[tag.id];
    }
  }

  deleteTag(categoryId: number, tag: Tag) {
    this.confirmationService.confirm({
      message: 'Wollen sie das Tag wirklich löschen?',
      header: 'Löschen bestätigen',
      acceptLabel: 'Löschen',
      acceptIcon: 'pi pi-trash mr-2',
      rejectLabel: 'Abbrechen',
      accept: () => {
        this.adminService.tags.deleteTag(tag.id).subscribe({
          next: () => {
            this.tagsPerCategory[categoryId] = this.tagsPerCategory[categoryId].filter(value => value.id !== tag.id);
            this.messageService.add({
              summary: 'Tag wurde gelöscht.',
              severity: 'success'
            });
          }
        });
      }
    });
  }
}